import { message } from 'ant-design-vue'
import Fuse, { IFuseOptions } from 'fuse.js'

import { DynamicIdParams, ISearchAction, ISearchAnchor } from '../types'

/** 单个函数类型 */
type SingleFunction = (...args: any) => any

/** 函数或函数数组 */
type FunctionOrArray = SingleFunction | SingleFunction[]

interface PageEventConfig {
  /** 是否是缓存页面，缓存页面不检查释放 */
  isKeepAlive?: boolean
  /** 注册的函数（每个函数名对应一个函数数组） */
  fns: Record<string, SingleFunction[]>
  /** 注册时间戳 */
  registeredAt: number
}

interface PageReadyResolver {
  /** 页面就绪的 Promise */
  promise: Promise<void>
  /** resolve 函数 */
  resolve: () => void
  /** 是否已就绪 */
  isReady: boolean
  /** 创建时间戳 */
  createdAt: number
}

/**
 * @description 搜索注册类，负责数据存储、搜索数据组装，并配合 Navigate 执行导航
 */
class SearchRegistry {
  static instance: SearchRegistry | null = null
  /** 搜索锚点 */
  private searchAnchors: ISearchAnchor[] = []
  /** 搜索路径为 app 内，内置提供的 query 数据源 */
  private appQueryData: Record<string, any> = {}
  /** 搜索路径为 app 内，内置提供的 path 数据源 */
  private appPathData: Record<string, any> = {}
  /** 动态ID参数数据源（按路由路径存储） */
  private dynamicIdParams: Record<string, DynamicIdParams> = {}
  /** Navigate 实例 */
  private navigate: Navigate = Navigate.getInstance()
  /** 路由管理器实例 */
  private routerManager: RouterManager = RouterManager.getInstance()
  /** Fuse 搜索实例 */
  private fuse: Fuse<ISearchAnchor> | null = null
  /** Fuse 配置选项 */
  private fuseOptions: IFuseOptions<ISearchAnchor> = {
    // 搜索的字段及权重
    keys: [
      { name: 'title', weight: 3 },
      { name: 'description', weight: 2 },
      { name: 'keywords', weight: 1.5 },
    ],
    // 模糊匹配阈值：0.0 完全匹配，1.0 完全模糊
    threshold: 0.4,
    // 是否包含匹配分数
    includeScore: true,
    // 是否包含匹配位置信息
    includeMatches: true,
    // 忽略位置
    ignoreLocation: true,
    // 最小匹配字符长度（降低到1，允许单字搜索）
    minMatchCharLength: 1,
    // 是否排序
    shouldSort: true,
    // 匹配算法距离（增加容错范围）
    distance: 100,
  }

  constructor(anchors: ISearchAnchor[] = []) {
    if (SearchRegistry.instance) {
      return SearchRegistry.instance as SearchRegistry
    }
    SearchRegistry.instance = this as SearchRegistry
    this.setAnchors(anchors)
    // 建立与 Navigate 的关联，使 Navigate 能够获取动态参数
    this.navigate.setSearchRegistry(this)
  }
  /**
   * @description 设置搜索锚点
   * @param anchors 搜索锚点数组
   */
  setAnchors(anchors: ISearchAnchor[]) {
    this.searchAnchors = anchors
    this.initFuse()
  }
  /**
   * @description 设置 router 实例，启用路由监听功能
   * @param router Vue Router 实例
   */
  setupRouter(router: any) {
    this.routerManager.setupRouter(router, this.navigate)
  }

  /**
   * @description 初始化 Fuse 搜索实例
   */
  private initFuse() {
    this.fuse = new Fuse(this.searchAnchors, this.fuseOptions)
  }

  /**
   * @description 更新 Fuse 配置选项
   * @param options 部分或完整的 Fuse 配置选项
   */
  updateFuseOptions(options: Partial<IFuseOptions<ISearchAnchor>>) {
    this.fuseOptions = { ...this.fuseOptions, ...options }
    this.initFuse()
  }

  /**
   * @description 注册新的搜索锚点
   * @param anchors 搜索锚点数组
   */
  registerSearchAnchor(anchors: ISearchAnchor[]) {
    this.searchAnchors.push(...anchors)
    // 重新初始化 Fuse 实例
    this.initFuse()
  }

  /**
   * @description 注册新的 app 内 query 数据源
   * @param data query 数据
   */
  registerAppQueryData(data: Record<string, any>) {
    this.appQueryData = { ...this.appQueryData, ...data }
  }

  /**
   * @description 注册新的 app 内 path 数据源
   * @param data path 数据
   */
  registerAppPathData(data: Record<string, any>) {
    this.appPathData = { ...this.appPathData, ...data }
  }

  /**
   * @description 注册页面的动态ID参数（用于替换选择器中的占位符）
   * @param pageRouter 页面路由路径
   * @param params 动态ID参数对象，如 { id: '123', lineId: '456' }
   * @example
   * // 在页面组件 onMounted 中调用
   * searchRegistry.registerDynamicParams('/equipment', { id: '123', type: 'A' })
   */
  registerDynamicParams(pageRouter: string, params: DynamicIdParams) {
    this.dynamicIdParams[pageRouter] = {
      ...this.dynamicIdParams[pageRouter],
      ...params,
    }
  }

  /**
   * @description 获取页面的动态ID参数
   * @param pageRouter 页面路由路径，不传则获取当前路由的参数
   * @returns 动态ID参数对象
   */
  getDynamicParams(pageRouter?: string): DynamicIdParams {
    const path = pageRouter || this.routerManager.getCurrentPath()
    return this.dynamicIdParams[path] || {}
  }

  /**
   * @description 清除页面的动态ID参数（在组件销毁时调用，避免内存泄漏）
   * @param pageRouter 页面路由路径
   */
  clearDynamicParams(pageRouter: string) {
    delete this.dynamicIdParams[pageRouter]
  }

  /**
   * @description 获取所有搜索锚点
   */
  getSearchAnchors() {
    return this.searchAnchors
  }

  /**
   * @description 根据关键词搜索锚点（使用 Fuse.js 模糊搜索）
   * @param keyword 关键词
   * @returns 搜索结果，按照匹配度排序
   */
  searchByKeyword(keyword: string): any[] {
    // 如果没有关键词，返回所有锚点
    if (!keyword || !keyword.trim()) {
      return this.searchAnchors.map((item) => ({ item, matches: [] }))
    }
    // 如果 Fuse 实例不存在，降级到简单搜索
    if (!this.fuse) {
      const lowerKeyword = keyword.toLowerCase()
      const filtered = this.searchAnchors.filter((anchor) => {
        return (
          anchor.title.toLowerCase().includes(lowerKeyword) ||
          anchor.keywords.some((kw: string) =>
            kw.toLowerCase().includes(lowerKeyword)
          ) ||
          anchor.description.toLowerCase().includes(lowerKeyword)
        )
      })
      return filtered.map((item) => ({ item, matches: [] }))
    }
    // 使用 Fuse.js 进行模糊搜索，返回包含 matches 的完整结果
    const results = this.fuse.search(keyword.trim())
    // 返回搜索结果（包含 item 和 matches），已经按照匹配度排序
    return results
  }

  /**
   * @description 组装导航数据（拼接 query 和 path 参数）
   * @param anchor 搜索锚点
   */
  private assembleNavigationData(anchor: ISearchAnchor) {
    const { navigationToApp } = anchor
    const { path, query = [], pathParam = [] } = navigationToApp
    // 组装 query 参数
    const queryObj: Record<string, any> = {
      ...(anchor.navigationToApp?.fixedData || {}),
    }
    query.forEach((item: any) => {
      const value = this.appQueryData[item.key]
      if (value !== undefined) {
        queryObj[item.key] = value
      }
    })
    // 组装 path 参数
    let finalPath = path
    pathParam.forEach((item: any) => {
      const value = this.appPathData[item.key]
      if (value !== undefined) {
        finalPath = finalPath.replace(`:${item.key}`, value)
      }
    })
    return {
      path: finalPath,
      query: queryObj,
    }
  }

  /**
   * @description 检查当前路由是否与目标路由相同
   * @param router Vue Router 实例
   * @param targetPath 目标路径
   * @param targetQuery 目标 query 参数
   */
  private isSameRoute(
    router: any,
    targetPath: string,
    targetQuery: Record<string, any>
  ): boolean {
    if (!router || !router.currentRoute) {
      return false
    }

    const currentRoute = router.currentRoute
    const currentPath = currentRoute.path
    const currentQuery = currentRoute.query || {}

    // 路径不同，肯定不是同一个路由
    if (currentPath !== targetPath) {
      return false
    }

    // 检查 query 参数是否相同
    const targetQueryKeys = Object.keys(targetQuery)
    const currentQueryKeys = Object.keys(currentQuery)

    // 如果都没有 query 参数，且路径相同，则认为是同一个路由
    if (targetQueryKeys.length === 0 && currentQueryKeys.length === 0) {
      return true
    }

    // 检查所有目标 query 参数是否都匹配
    return targetQueryKeys.every(
      (key) => currentQuery[key] === targetQuery[key]
    )
  }

  /**
   * @description 执行搜索导航
   * @param anchor 搜索锚点
   * @param router Vue Router 实例（用于应用内导航）
   * @param options 配置选项
   */
  async navigateToAnchor(
    anchor: ISearchAnchor,
    router?: any,
    options?: {
      /** 等待页面就绪的超时时间（毫秒），默认 5000ms */
      waitTimeout?: number
      /** 是否跳过等待页面就绪，默认 false */
      skipWaitReady?: boolean
    }
  ) {
    if (anchor.target === 'internet') {
      // 外部链接，直接打开
      window.open(anchor.navigationToInternet.url, '_blank')
      return
    }

    // 应用内导航
    const { path, query } = this.assembleNavigationData(anchor)

    // 检查是否已经在目标页面
    const isSamePage = this.isSameRoute(router, path, query)

    // 这样页面 onMounted 中的 registerEvent 就能找到 resolver
    const hasActions = anchor.actions && anchor.actions.length > 0
    const needWaitReady = !options?.skipWaitReady && !isSamePage && hasActions
    if (needWaitReady) {
      // 预先创建 Promise resolver，确保页面注册事件时能找到
      this.navigate.preparePageReady(path)
    }

    // 如果不在同一页面，才进行路由跳转
    if (!isSamePage && router) {
      try {
        await router.push({ path, query })
      } catch (error) {
        // 捕获路由跳转错误（例如重复导航）
        console.warn('[SearchRegistry] 路由跳转警告:', error)
      }
    }

    // 执行滚动和动作（等待页面就绪）
    return await this.executeAnchorActions(anchor, path, {
      waitTimeout: options?.waitTimeout,
      skipWaitReady: options?.skipWaitReady,
      isPageChanged: !isSamePage,
    })
  }

  /**
   * @description 执行锚点的滚动和动作
   * @param anchor 搜索锚点
   * @param path 路径（用于执行动作）
   * @param options 配置选项
   */
  private async executeAnchorActions(
    anchor: ISearchAnchor,
    path: string,
    options?: {
      waitTimeout?: number
      skipWaitReady?: boolean
      isPageChanged?: boolean
    }
  ) {
    // 如果页面发生了跳转，且有动作需要执行，等待页面就绪
    const hasActions = anchor.actions && anchor.actions.length > 0
    const needWaitReady =
      !options?.skipWaitReady && options?.isPageChanged && hasActions

    if (needWaitReady) {
      await this.navigate.waitForPageReady(path, options?.waitTimeout)
    } else {
      // 如果不需要等待（页面未跳转或没有动作），简单等待 DOM 更新
      await new Promise((resolve) => setTimeout(resolve, 50))
    }
    /** 定位前要执行的动作 */
    const positionBeforeAnchor: ISearchAction[] = []
    /** 定位后要执行的动作 */
    const positionAfterAnchor: ISearchAction[] = []
    anchor.actions?.forEach((v) => {
      if (v.executeAfterAnchor) {
        positionAfterAnchor.push(v)
      } else {
        positionBeforeAnchor.push(v)
      }
    })
    // 执行动作
    if (positionBeforeAnchor.length) {
      await this.navigate.executeActions(path, positionBeforeAnchor)
    }
    /** 目标元素 */
    let targetElement: HTMLElement | undefined
    // 滚动到目标元素
    if (anchor.anchorElement?.selector) {
      // 先处理动态ID，再传给 scrollToElement
      const processedSelector = this.navigate.processDynamicId(
        anchor.anchorElement.selector,
        anchor.anchorElement.isDynamic
      )
      // 延迟滚动。避免 ui 界面刷新不及时，但是无法完全避免此问题
      await new Promise((resolve) => setTimeout(resolve, 500))
      targetElement = await this.navigate.scrollToElement(
        processedSelector,
        anchor.anchorElement.type,
        anchor.anchorElement.highlight,
        anchor.anchorElement.timeout
      )
    }
    // 执行动作
    if (positionAfterAnchor.length) {
      await this.navigate.executeActions(path, positionAfterAnchor)
    }
    return targetElement
  }

  static getInstance(anchors: ISearchAnchor[] = []) {
    if (!SearchRegistry.instance) {
      new SearchRegistry(anchors)
    }
    return SearchRegistry.instance as SearchRegistry
  }
}

/**
 * @description 路由管理类，负责路由监听和页面切换检测
 */
class RouterManager {
  static instance: RouterManager | null = null
  private router: any = null
  private routerUnwatchBefore: (() => void) | null = null
  private routerUnwatchAfter: (() => void) | null = null
  /** Navigate 实例引用 */
  private navigate: Navigate | null = null
  /** 即将离开的路由路径（在 beforeEach 中记录） */
  private leavingPath: string | null = null
  /** 即将进入的路由路径（在 beforeEach 中记录） */
  private enteringPath: string | null = null

  constructor() {
    if (RouterManager.instance) {
      return RouterManager.instance as RouterManager
    }
    RouterManager.instance = this as RouterManager
  }

  /**
   * @description 设置 router 实例并监听路由变化
   * @param router Vue Router 实例
   * @param navigate Navigate 实例
   */
  setupRouter(router: any, navigate: Navigate) {
    this.router = router
    this.navigate = navigate

    // 监听路由变化 - beforeEach（记录即将离开的路由）
    if (router && typeof router.beforeEach === 'function') {
      this.routerUnwatchBefore = router.beforeEach(
        (to: any, from: any, next: any) => {
          // 记录即将离开的路由
          this.leavingPath = from.path
          this.enteringPath = to.path
          next()
        }
      )
    }

    // 监听路由变化 - afterEach（路由跳转完成后检查）
    if (router && typeof router.afterEach === 'function') {
      this.routerUnwatchAfter = router.afterEach(() => {
        // 在路由跳转完成后，检查刚才离开的页面
        if (this.leavingPath) {
          // 使用 nextTick 确保组件已经完全销毁
          this.checkPageEventsReleased(
            this.leavingPath,
            this.enteringPath || ''
          )
          this.leavingPath = null
        }
      })
    }
  }

  /**
   * @description 检查页面是否释放了事件（在组件销毁后检查）
   * @param fromPath 离开的路由路径
   */
  private checkPageEventsReleased(fromPath: string, toPath: string) {
    if (!this.navigate) return
    const navigate = this.navigate
    // 使用 setTimeout 确保 onBeforeUnmount 已经执行完成
    setTimeout(() => {
      const isDev = process.env.NODE_ENV === 'development'
      const hasUnreleasedEvents =
        toPath !== fromPath && navigate.checkUnreleasedEvents(fromPath)
      if (hasUnreleasedEvents && isDev) {
        console.error(
          `[SearchRegistry] ❌ 页面 ${fromPath} 存在未释放的搜索事件，请在组件销毁时调用 unregisterEvent 方法释放`
        )
      }
    }, 100) // 延迟 100ms，确保组件销毁完成
  }

  /**
   * @description 获取当前路由路径
   */
  getCurrentPath(): string {
    if (!this.router || !this.router.currentRoute) {
      return ''
    }
    return this.router.currentRoute.path || ''
  }

  /**
   * @description 销毁路由监听
   */
  destroy() {
    if (this.routerUnwatchBefore) {
      this.routerUnwatchBefore()
      this.routerUnwatchBefore = null
    }
    if (this.routerUnwatchAfter) {
      this.routerUnwatchAfter()
      this.routerUnwatchAfter = null
    }
  }

  static getInstance() {
    if (!RouterManager.instance) {
      new RouterManager()
    }
    return RouterManager.instance as RouterManager
  }
}

/**
 * @description 导航工具类，负责页面事件注册、释放和执行 actions
 */
class Navigate {
  static instance: Navigate | null = null
  /** 页面注册的相关事件及配置 */
  private pageRegisteredEvents: Record<string, PageEventConfig> = {}
  /** 路由管理器 */
  private routerManager: RouterManager = RouterManager.getInstance()
  /** 页面就绪状态管理 */
  private pageReadyResolvers: Record<string, PageReadyResolver> = {}
  /** SearchRegistry 实例引用（用于获取动态参数） */
  private searchRegistry: SearchRegistry | null = null
  /** 默认超时时间（毫秒） */
  private readonly DEFAULT_TIMEOUT = 5000

  constructor() {
    if (Navigate.instance) {
      return Navigate.instance as Navigate
    }
    Navigate.instance = this as Navigate
  }

  /**
   * @description 设置 SearchRegistry 实例引用
   * @param registry SearchRegistry 实例
   */
  setSearchRegistry(registry: SearchRegistry) {
    this.searchRegistry = registry
  }

  /**
   * @description 创建页面就绪 Promise
   * @param pageRouter 页面路由
   */
  private createPageReadyPromise(pageRouter: string): PageReadyResolver {
    const resolver: Partial<PageReadyResolver> = {
      isReady: false,
      createdAt: Date.now(),
    }

    resolver.promise = new Promise<void>((resolve) => {
      resolver.resolve = resolve
    })

    this.pageReadyResolvers[pageRouter] = resolver as PageReadyResolver
    return resolver as PageReadyResolver
  }

  /**
   * @description 预先准备页面就绪的 Promise（在路由跳转前调用）
   * @param pageRouter 页面路由
   */
  preparePageReady(pageRouter: string): void {
    // 如果页面已经注册事件，不需要准备
    if (this.pageRegisteredEvents[pageRouter]) {
      return
    }

    // 如果已经有等待的 Promise 且未就绪，不需要重复创建
    const existingResolver = this.pageReadyResolvers[pageRouter]
    if (existingResolver && !existingResolver.isReady) {
      return
    }

    // 创建新的等待 Promise
    this.createPageReadyPromise(pageRouter)
  }

  /**
   * @description 等待页面就绪
   * @param pageRouter 页面路由
   * @param timeout 超时时间（毫秒），默认 5000ms
   */
  async waitForPageReady(
    pageRouter: string,
    timeout: number = this.DEFAULT_TIMEOUT
  ): Promise<boolean> {
    // 如果页面已经注册事件，立即返回
    const existingConfig = this.pageRegisteredEvents[pageRouter]
    if (existingConfig) {
      return true
    }

    // 如果已经有等待的 Promise，使用现有的
    let resolver = this.pageReadyResolvers[pageRouter]
    if (!resolver || resolver.isReady) {
      resolver = this.createPageReadyPromise(pageRouter)
    }

    // 设置超时
    const timeoutPromise = new Promise<boolean>((resolve) => {
      setTimeout(() => {
        if (process.env.NODE_ENV === 'development') {
          console.warn(
            `[Navigate] 等待页面 ${pageRouter} 就绪超时 (${timeout}ms)`
          )
        }
        resolve(false)
      }, timeout)
    })

    // 竞速：要么页面就绪，要么超时
    const readyPromise = resolver.promise.then(() => true)

    return Promise.race([readyPromise, timeoutPromise])
  }

  /**
   * @description 注册页面内在后台配置的动作对应的函数/同时也会通知查询器已就绪可以开始执行
   * @param pageRouter 页面路由
   * @param fns 该页面注册的函数集合，支持单个函数或函数数组
   * @param options 配置选项
   * @example
   * // 注册单个函数
   * registerEvent('/page', { action1: () => {} })
   * // 注册函数数组
   * registerEvent('/page', { action1: [fn1, fn2, fn3] })
   * // 混合使用
   * registerEvent('/page', { action1: fn1, action2: [fn2, fn3] })
   */
  registerEvent(
    pageRouter: string,
    fns: Record<string, FunctionOrArray>,
    options?: { isKeepAlive?: boolean }
  ) {
    this.addExtraEvent(pageRouter, fns, options)
    // 通知页面就绪
    const resolver = this.pageReadyResolvers[pageRouter]
    if (resolver && !resolver.isReady) {
      resolver.isReady = true
      resolver.resolve()
    }
  }

  /**
   * @description 添加额外的事件但不会触发页面就绪
   * @param pageRouter 页面路由
   * @param fns 该页面注册的函数集合，支持单个函数或函数数组
   */
  addExtraEvent(
    pageRouter: string,
    fns: Record<string, FunctionOrArray>,
    options?: { isKeepAlive?: boolean }
  ) {
    const existingConfig = this.pageRegisteredEvents[pageRouter]
    const normalizedFns = normalizeFnsRecord(fns)

    // 合并函数数组：如果已存在同名函数，将新函数追加到数组中
    const mergedFns: Record<string, SingleFunction[]> = {
      ...(existingConfig?.fns || {}),
    }

    for (const key in normalizedFns) {
      if (mergedFns[key]) {
        // 已存在，追加到数组末尾
        mergedFns[key] = [...mergedFns[key], ...normalizedFns[key]]
      } else {
        // 不存在，直接赋值
        mergedFns[key] = normalizedFns[key]
      }
    }

    this.pageRegisteredEvents[pageRouter] = {
      fns: mergedFns,
      isKeepAlive: options?.isKeepAlive ?? existingConfig?.isKeepAlive ?? false,
      registeredAt: Date.now(),
    }
  }

  /**
   * @description 释放页面注册的事件
   * @param pageRouter 页面路由
   */
  unregisterEvent(pageRouter: string) {
    if (this.pageRegisteredEvents[pageRouter]) {
      delete this.pageRegisteredEvents[pageRouter]

      // 清理页面就绪状态
      if (this.pageReadyResolvers[pageRouter]) {
        delete this.pageReadyResolvers[pageRouter]
      }
    }
  }

  /**
   * @description 检查页面是否有未释放的事件（内部方法）
   * @param pageRouter 页面路由
   */
  checkUnreleasedEvents(pageRouter: string): boolean {
    const config = this.pageRegisteredEvents[pageRouter]
    // 如果没有注册事件，或者是缓存页面，不需要检查
    if (!config || config.isKeepAlive) {
      return false
    }
    return true
  }

  /**
   * @description 执行指定页面的指定动作（会按顺序执行该动作名下的所有函数）
   * @param pageRouter 页面路由
   * @param fnName 函数名
   * @param payload 传递给函数的参数
   */
  async executeAction(pageRouter: string, fnName: string, payload?: any) {
    const pageConfig = this.pageRegisteredEvents[pageRouter]
    if (!pageConfig) {
      return
    }
    const fns = pageConfig.fns[fnName]
    if (!fns || !Array.isArray(fns) || fns.length === 0) {
      return
    }

    // 按顺序执行所有函数
    for (let i = 0; i < fns.length; i++) {
      const fn = fns[i]
      if (typeof fn !== 'function') {
        continue
      }
      try {
        await fn(payload)
      } catch (error) {
        console.error(`执行函数 ${fnName}[${i}] 失败:`, error)
      }
    }
  }

  /**
   * @description 获取已注册的页面列表
   */
  getRegisteredPages(): string[] {
    return Object.keys(this.pageRegisteredEvents)
  }

  /**
   * @description 执行一系列动作
   * @param pageRouter 页面路由
   * @param actions 动作列表
   */
  async executeActions(pageRouter: string, actions: ISearchAction[] = []) {
    for (const action of actions) {
      // 每一个执行中间做一个 50 毫秒的延迟
      await new Promise((resolve) => setTimeout(resolve, 50))
      if (action.type === 'delay') {
        const ms = Number(action.ms) || 0
        await new Promise((resolve) => setTimeout(resolve, ms))
      } else if (action.type === 'invokeAction' && action.fn) {
        await this.executeAction(pageRouter, action.fn, action.payload)
      } else if (action.type === 'waitForCondition' && action.fn) {
        await this.executeWaitForCondition(
          pageRouter,
          action.fn,
          action.returnValue
        )
      } else if (action.type === 'anchorTo' && action.anchorConfig) {
        // 先处理动态ID，再传给 scrollToElement
        const processedSelector = this.processDynamicId(
          action.anchorConfig.selector,
          action.anchorConfig.isDynamic
        )
        await new Promise((resolve) => setTimeout(resolve, 50))
        await this.scrollToElement(
          processedSelector,
          action.anchorConfig.type,
          action.anchorConfig.highlight,
          action.anchorConfig.timeout
        )
      }
    }
    // 每一个动作都有 50毫秒的延迟
    await new Promise((resolve) => setTimeout(resolve, 50))
  }
  /**
   * @description 执行等待条件（只执行第一个注册的函数）
   * @param pageRouter
   * @param fnName
   * @param returnValue
   */
  async executeWaitForCondition(
    pageRouter: string,
    fnName: string,
    returnValue: any
  ) {
    const pageConfig = this.pageRegisteredEvents[pageRouter]
    if (!pageConfig) {
      return
    }
    const fns = pageConfig.fns[fnName]
    if (!fns || !Array.isArray(fns) || fns.length === 0) {
      return
    }

    // 对于等待条件，只执行第一个函数（因为是等待某个特定条件）
    const fn = fns[0]
    if (typeof fn !== 'function') {
      return
    }

    /** 每次执行间隔 200 毫秒 */
    const INTERVAL_TIME = 200
    /** 最大执行数 15 秒 */
    const MAX_EXECUTE_COUNT = (15 * 1000) / INTERVAL_TIME
    for (let i = 0; i < MAX_EXECUTE_COUNT; i++) {
      const result = await fn()
      // eslint-disable-next-line
      if (result == returnValue) {
        return true
      }
      await new Promise((resolve) => setTimeout(resolve, INTERVAL_TIME))
    }
    console.warn(`未找到条件: ${fnName}`)
    message.warning(`未能成功找到指定条件: ${fnName}`)
    return false
  }

  /**
   * @description 处理动态ID - 将选择器中的占位符替换为实际值
   * @param selector 原始选择器（可能包含 {key} 形式的占位符）
   * @param isDynamic 是否为动态ID
   * @returns 处理后的选择器
   * @example
   * processDynamicId('equipment_{id}', true) // 'equipment_123'（假设注册了 { id: '123' }）
   * processDynamicId('line_{lineId}_machine_{machineId}', true)
   * // 'line_1_machine_2'（假设注册了 { lineId: '1', machineId: '2' }）
   */
  processDynamicId(selector: string, isDynamic?: boolean): string {
    // 如果不是动态ID，直接返回原选择器
    if (!isDynamic) {
      return selector
    }
    // 如果没有 searchRegistry 实例，无法获取参数，警告并返回原选择器
    if (!this.searchRegistry) {
      return selector
    }

    // 获取当前页面的动态参数
    const params = this.searchRegistry.getDynamicParams()

    // 使用正则替换所有 {xxx} 形式的占位符
    const processedSelector = selector.replace(/\{(\w+)\}/g, (match, key) => {
      const value = params[key]
      if (value === undefined || value === null) {
        console.warn(
          `[Navigate] 动态ID参数缺失: ${key}，选择器: ${selector}，当前参数:`,
          params
        )
        return match // 保留原占位符
      }
      return String(value)
    })

    // 开发环境下打印处理日志
    if (
      process.env.NODE_ENV === 'development' &&
      selector !== processedSelector
    ) {
      console.log(
        `[Navigate] 动态ID处理: ${selector} -> ${processedSelector}`,
        params
      )
    }

    return processedSelector
  }

  /**
   * @description 滚动到指定元素（只负责滚动，不处理动态ID）
   * @param selector 元素选择器（应该是处理好的最终选择器）
   * @param type 选择器类型
   * @param timeout 超时时间
   */
  async scrollToElement(
    selector: string,
    type: 'id' | 'queryString',
    isHighlight = true,
    timeout = 5000
  ) {
    let element: HTMLElement | null = null
    const findElement = () => {
      if (type === 'id') {
        element = document.getElementById(selector)
      } else if (type === 'queryString') {
        element = document.querySelector(selector) as HTMLElement
      }
      return element
    }
    // 执行秒数外部决定，但是每500毫秒执行一次
    const COUNT = timeout / 500
    // 执行 5000 毫秒的查找 每 500 毫秒查找一次 10 次还没找到才退出
    for (let i = 0; i < COUNT; i++) {
      const element = findElement()
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' })
        if (isHighlight) {
          autoRemoveHighlight(element)
        }
        return element
      }
      await new Promise((resolve) => setTimeout(resolve, 500))
    }
    console.warn(`未找到元素: ${selector}`)
    message.warning(
      `未能成功找到指定跳转目标: ${selector}，请注意是否是功能未开启`
    )
  }

  static getInstance() {
    if (!Navigate.instance) {
      new Navigate()
    }
    return Navigate.instance as Navigate
  }
}

/**
 * @description 规范化函数输入，将单个函数或函数数组统一转换为数组
 * @param fnOrArray 单个函数或函数数组
 * @returns 函数数组
 */
function normalizeFunctions(fnOrArray: FunctionOrArray): SingleFunction[] {
  return Array.isArray(fnOrArray) ? fnOrArray : [fnOrArray]
}

/**
 * @description 规范化函数集合，将 Record<string, 函数|数组> 转换为 Record<string, 数组>
 * @param fns 函数集合
 * @returns 规范化后的函数集合
 */
function normalizeFnsRecord(
  fns: Record<string, FunctionOrArray>
): Record<string, SingleFunction[]> {
  const result: Record<string, SingleFunction[]> = {}
  for (const key in fns) {
    result[key] = normalizeFunctions(fns[key])
  }
  return result
}

/**
 * @description 高亮元素
 * @param el 元素
 */
function highlight(el: HTMLElement) {
  if (!el) return
  el.classList?.add('global-search-highlight-pulse')
}

/**
 * @description 移除高亮元素
 * @param el 元素
 */
function removeHighlight(el: HTMLElement) {
  if (!el) return
  el.classList?.remove('global-search-highlight-pulse')
}
/** @description 添加后自定移除 */
function autoRemoveHighlight(el: HTMLElement, timeout = 5000) {
  if (!el) return
  highlight(el)
  setTimeout(() => {
    removeHighlight(el)
  }, timeout)
}

export { Navigate, RouterManager, SearchRegistry }
export default SearchRegistry
